import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appLinks } from 'src/constants/common.constants';
import { FaqCategoriesMenu } from 'src/features/common/faq/components/faq-categories-menu';
import { FaqPageSection } from 'src/features/common/faq/components/faq-page-section';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { ILocale } from 'src/features/common/faq/containers/highlighted-questions/types';
import { TechPageHeader } from 'src/features/common/tech-pages/components/tech-page-header';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { useWindowListener } from 'src/hooks/use-window-listener';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { PageLayout } from 'src/ui/common/layouts/page-layout';
import { Visible } from 'src/ui/common/layouts/visible';
import { Accordion } from 'src/ui/common/molecules/accordion';
import {
    StyledFaqPageContent,
    StyledImageContainer,
    StyledImg,
    StyledList,
    StyledListStep,
    StyledParagraph,
} from './faq-container.styles';

export const categories = [
    { title: 'General information', anchor: 'general-information' },
    { title: 'Buying', anchor: 'buying' },
    { title: 'Selling', anchor: 'selling' },
    { title: 'Wallet', anchor: 'wallet' },
    { title: 'Integration information', anchor: 'integration' },
];

export const FaqContainer = () => {
    const { t } = useTranslation(['pages']);
    const { asPath } = useRouter();
    const { projectName } = useUniverse();
    const isMounted = useIsMounted();
    const [activeSection, setActiveSection] = useState<string>(categories[0].anchor);
    const belowElementsRef = useRef<Element[]>();

    const locale: ILocale = useMemo(
        () => ({
            ...t('faq', { returnObjects: true, faqCompanyName: projectName }),
        }),
        [t, projectName]
    );

    const [, initialOpenedId] = useMemo(
        () => asPath.split('#'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useWindowListener(
        'scroll',
        () => {
            const below = belowElementsRef.current?.reduce<Element[]>((acc, curr) => {
                if (curr.getBoundingClientRect().top > 0) {
                    acc.push(curr);
                }

                return acc;
            }, []);

            if (below?.length) {
                setActiveSection(below[0].id);
            }
        },
        100
    );

    useEffect(() => {
        isMounted(() => {
            belowElementsRef.current = [...document.querySelectorAll('[data-observablesection]')];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <TechPageHeader
                title={`${projectName} FAQ`}
                subtitle="Search our knowledge base or browse categories below."
                type="faq"
            />
            <PageLayout fullWidth={false}>
                <StyledFaqPageContent>
                    <Visible view="desktop">
                        <FaqCategoriesMenu active={activeSection} categories={categories} />
                    </Visible>
                    <FlexBox flexDirection="column">
                        <Accordion initialOpenedId={initialOpenedId}>
                            <FaqPageSection title="General information" anchor="general-information">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    {locale?.whatIsCompany?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                                    <StyledParagraph>
                                        {projectName} only offers selected elite NFTs created by well-known profiles and
                                        brands. This way you can make sure your NFT has a value beyond the normal. In a
                                        world where the process of selecting the right NFT is pure chaos and completely
                                        unmanageable,
                                        {projectName} has segmented, selected and offers only the best for you.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_START}>
                                    {locale?.howToStart?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_START}>
                                    <StyledListStep>
                                        <li>Register on the website - NFTSuperLab.io.</li>
                                        <li>Set up your CryptoX wallet.</li>
                                        <li>Connect your CryptoX wallet to your account on NFTSuperLab.</li>
                                        <li>You can now start buying NFTs on the website.</li>
                                    </StyledListStep>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    {locale?.whatIsNFT?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                                    <Spacing pr="20px">
                                        {locale?.whatIsNFT?.paragraphs?.map((item) => (
                                            <StyledParagraph key={item}>{item}</StyledParagraph>
                                        ))}
                                        <StyledList>
                                            {locale?.whatIsNFT?.fungibleItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                        <StyledParagraph>{locale?.whatIsNFT?.nonFungible}</StyledParagraph>
                                        <StyledList>
                                            {locale?.whatIsNFT?.nonFungibleItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                        <StyledParagraph>{locale?.whatIsNFT?.publiclyOwnership}</StyledParagraph>
                                        <StyledList>
                                            {locale?.whatIsNFT?.publiclyOwnershipItems?.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </StyledList>
                                    </Spacing>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.PLATFORM_FEES}>
                                    {locale?.whatIsFee?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.PLATFORM_FEES}>
                                    <StyledParagraph>
                                        On the marketplace, a 10% platform fee will be deducted from all sales. This
                                        means that over and above the royalty fee set, a 10% platform fee will be
                                        deducted from the sale. This only affects the seller - not the buyer.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    {locale?.whatIsSmartContract?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_SC}>
                                    {locale?.whatIsSmartContract?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_SC_WORK}>
                                    {locale?.howDoesSmartContractWork?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_SC_WORK}>
                                    {locale?.howDoesSmartContractWork?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_VIEW_SC}>
                                    {locale?.viewNFTSmartContract?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_VIEW_SC}>
                                    <StyledParagraph>{locale?.viewNFTSmartContract?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Buying" anchor="buying">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    {locale?.paymentWhenBuying?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_USE_FOR_PAYMENT}>
                                    <StyledParagraph>
                                        You will need to connect your CryptoX wallet to buy an NFT on the {projectName}{' '}
                                        marketplace. After connecting your wallet you can choose to buy NFTs with crypto
                                        (CCD) or credit card. After the payment is completed, the NFT will be
                                        automatically transferred to your wallet.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_PURCHASE_AUCTION}>
                                    {locale?.howToPurchaseOnAuction?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_PURCHASE_AUCTION}>
                                    <StyledListStep>
                                        {locale?.howToPurchaseOnAuction?.paragraphs?.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </StyledListStep>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Selling" anchor="selling">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    {locale?.whatIsMinting?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                                    {locale?.whatIsMinting?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    {locale?.areNftRefundable?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ARE_NFTS_REFUNDABLE}>
                                    <StyledParagraph>{locale?.areNftRefundable?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    {locale?.whereDoIFindNFT?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_FIND_NFT}>
                                    <StyledParagraph>
                                        The NFT will be stored in your CryptoX wallet. You can also see it on your
                                        profile on {projectName}.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.ROYALTIES}>
                                    {locale?.howDoRoyaltiesWork?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.ROYALTIES}>
                                    {locale?.howDoRoyaltiesWork?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    {locale?.capOfRoyalties?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAP_OF_ROYALTIES}>
                                    <StyledParagraph>{locale?.capOfRoyalties?.paragraph}</StyledParagraph>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Wallet" anchor="wallet">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    {locale?.wallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_WALLET_IS_USED}>
                                    <StyledParagraph>
                                        We offer support for the CryptoX wallet. Note: CryptoX desktop app is not
                                        available yet.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    {locale?.otherWallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.OTHER_WALLET}>
                                    <StyledParagraph>
                                        At this stage, we will only offer support for CryptoX wallet mobile application.
                                        Note: CryptoX desktop app is not available yet.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    {locale?.downloadWallets?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.WHERE_CAN_I_DOWNLOAD_WALLETS}>
                                    <StyledParagraph>{locale?.downloadWallets?.cryptoX}</StyledParagraph>
                                    <Spacing pl="20px">
                                        <StyledParagraph>{locale?.downloadWallets?.clickLinks}</StyledParagraph>
                                        <Typography tag="p">
                                            {locale?.downloadWallets?.cryptoIOS}
                                            <a href={appLinks.appStore} target="_blank" rel="noreferrer">
                                                {appLinks.appStore}
                                            </a>
                                            <br />
                                            {locale?.downloadWallets?.cryptoAndroid}
                                            <a href={appLinks.googlePlay} target="_blank" rel="noreferrer">
                                                {appLinks.googlePlay}
                                            </a>
                                        </Typography>
                                    </Spacing>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    {locale?.connectUseCryptoX?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_CONNECT_CRYPTOX}>
                                    <div data-scrollable="true">
                                        <div>
                                            <StyledParagraph>{locale?.connectUseCryptoX?.paragraph}</StyledParagraph>
                                            <ul>
                                                <li>
                                                    <StyledParagraph>
                                                        {locale?.connectUseCryptoX?.onHeader}
                                                    </StyledParagraph>
                                                    {/* @TODO: Use Solana screenshot */}
                                                    <StyledImg
                                                        src="/faq/connect-wallet-1.webp"
                                                        alt="header wallet connect"
                                                    />
                                                </li>
                                                <li>
                                                    <Typography tag="p">
                                                        <StyledParagraph>
                                                            {locale?.connectUseCryptoX?.option}
                                                        </StyledParagraph>
                                                    </Typography>
                                                    <StyledImageContainer>
                                                        <StyledImg src="/faq/connect-wallet-2.webp" alt="header menu" />
                                                        <StyledImg
                                                            src="/faq/connect-wallet-3.webp"
                                                            alt="account settings"
                                                        />
                                                    </StyledImageContainer>
                                                </li>
                                            </ul>
                                            <Typography tag="p" semiBold>
                                                {locale?.connectUseCryptoX?.walletAddress}
                                            </Typography>
                                            <StyledImg src="/faq/wallet-list.webp" alt="wallet list" />
                                        </div>
                                    </div>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.MINIMUM_CCD_TRANSACT}>
                                    {locale?.minAmountCCD?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.MINIMUM_CCD_TRANSACT}>
                                    <StyledParagraph>{locale?.minAmountCCD?.paragraph}</StyledParagraph>
                                    <StyledParagraph>
                                        This (blockchain) gas fee you have to pay when your purchase an NFT on{' '}
                                        {projectName}.
                                    </StyledParagraph>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    {locale?.canIViewNFTOnMyWallet?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.CAN_VIEW_NFT_WALLET}>
                                    {locale?.canIViewNFTOnMyWallet?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                    <StyledListStep>
                                        {locale?.canIViewNFTOnMyWallet?.guideSteps?.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </StyledListStep>
                                </Accordion.ItemContent>
                            </FaqPageSection>
                            <FaqPageSection title="Integration information" anchor="integration">
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_ADD_CRYPTOWALLET}>
                                    {locale?.howToAddNFTToCryptoX?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_ADD_CRYPTOWALLET}>
                                    <Typography tag="p">
                                        <a href="/faq/cryptox-nft-tokens-import-api.docx" download>
                                            {locale?.howToAddNFTToCryptoX?.paragraph}
                                        </a>
                                    </Typography>
                                </Accordion.ItemContent>
                                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_INTEGRATE_CRYPTOWALLET_INTO_APP}>
                                    {locale?.howToIntegrateCryptoXInWebApp?.title}
                                </Accordion.ItemHeader>
                                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_INTEGRATE_CRYPTOWALLET_INTO_APP}>
                                    {locale?.howToIntegrateCryptoXInWebApp?.paragraphs?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                    <StyledList>
                                        {locale?.howToIntegrateCryptoXInWebApp?.components?.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </StyledList>
                                    <StyledParagraph>{locale?.howToIntegrateCryptoXInWebApp?.note}</StyledParagraph>
                                    <br />
                                    <StyledParagraph>
                                        {locale?.howToIntegrateCryptoXInWebApp?.webBridge}
                                    </StyledParagraph>
                                    <Typography tag="p">
                                        <StyledParagraph>
                                            {locale?.howToIntegrateCryptoXInWebApp?.integrate}
                                        </StyledParagraph>
                                        <a href="/faq/cwb-configuration-guide.docx" download>
                                            {locale?.howToIntegrateCryptoXInWebApp?.here}
                                        </a>{' '}
                                        .
                                    </Typography>
                                    <StyledParagraph>{locale?.howToIntegrateCryptoXInWebApp?.docker}</StyledParagraph>
                                    <StyledParagraph>
                                        {locale?.howToIntegrateCryptoXInWebApp?.transactions}
                                    </StyledParagraph>
                                    <Typography>
                                        <a
                                            href="/faq/Concordium-web-to-wallet-integration-protocol-beta-v0_6.docx"
                                            download
                                        >
                                            {locale?.howToIntegrateCryptoXInWebApp?.guideline}
                                        </a>
                                    </Typography>
                                    <br />
                                    <StyledParagraph>
                                        {locale?.howToIntegrateCryptoXInWebApp?.backendApi}
                                    </StyledParagraph>
                                    <Typography tag="p">
                                        <a href="/faq/cryptox-nft-tokens-import-api.docx" download>
                                            {locale?.howToIntegrateCryptoXInWebApp?.links[0]}
                                        </a>
                                    </Typography>
                                    <Typography tag="p">
                                        <a href="/faq/cryptoX-airdrop-campaigns-api.docx" download>
                                            {locale?.howToIntegrateCryptoXInWebApp?.links[1]}
                                        </a>
                                    </Typography>
                                    <br />
                                    {locale?.howToIntegrateCryptoXInWebApp?.backendConcordiumSync?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                    <StyledList>
                                        {locale?.howToIntegrateCryptoXInWebApp?.getActualStateForSmartcontract?.map(
                                            (item) => (
                                                <li key={item}>{item}</li>
                                            )
                                        )}
                                    </StyledList>
                                    <br />
                                    {locale?.howToIntegrateCryptoXInWebApp?.smartcontracts?.map((item) => (
                                        <StyledParagraph key={item}>{item}</StyledParagraph>
                                    ))}
                                </Accordion.ItemContent>
                            </FaqPageSection>
                        </Accordion>
                    </FlexBox>
                </StyledFaqPageContent>
            </PageLayout>
        </div>
    );
};